import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Mountain, Stethoscope, Users2, Building2 } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { travelTypes } from '../data/mock';
import TravelQuizModal from './TravelQuizModal';

const TravelTypes = () => {
  const [isQuizModalOpen, setIsQuizModalOpen] = useState(false);

  const iconMap = {
    honeymoon: Heart,
    adventure: Mountain,
    'medical-tourism': Stethoscope,
    family: Users2,
    'corporate-events': Building2
  };

  return (
    <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Travel Your
            <span className="block bg-gradient-to-r from-[#00DF98] to-[#FE4B79] bg-clip-text text-transparent">
              Way
            </span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Whether you're seeking romance, adventure, wellness, or family fun, 
            we have the perfect travel experiences crafted just for you.
          </p>
        </div>

        {/* Travel types grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-8">
          {travelTypes.map((type, index) => {
            const IconComponent = iconMap[type.id];
            return (
              <Card 
                key={type.id}
                className="group cursor-pointer overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-white"
              >
                <div className="relative overflow-hidden">
                  <div
                    className="h-48 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                    style={{ backgroundImage: `url(${type.image})` }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
                  
                  {/* Icon overlay */}
                  <div className="absolute top-4 right-4">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
                      <IconComponent size={20} className="text-white" />
                    </div>
                  </div>

                  {/* Content overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">
                      {type.title}
                    </h3>
                    <p className="text-white/90 text-sm">
                      {type.description}
                    </p>
                  </div>
                </div>

                <CardContent className="p-4">
                  <Button 
                    variant="outline"
                    size="sm"
                    className="w-full border-[#6C30ED]/30 text-[#6C30ED] hover:bg-[#6C30ED] hover:text-white font-medium transition-all duration-300 py-2"
                  >
                    Explore {type.title}
                    <ArrowRight size={14} className="ml-2 group-hover/btn:translate-x-1 transition-transform" />
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-16 text-center">
          <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12 border border-gray-100">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              Not Sure Which Style Suits You?
            </h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Take our quick travel personality quiz to discover your ideal travel style 
              and get personalized recommendations.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => setIsQuizModalOpen(true)}
                className="bg-gradient-to-r from-[#FE4B79] to-[#6C30ED] hover:from-[#fe3667] hover:to-[#5c28d4] text-white font-semibold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300"
              >
                Take Travel Quiz
              </Button>
              <Link to="/contact">
                <Button 
                  size="lg"
                  variant="outline"
                  className="border-2 border-[#FE4B79] text-[#FE4B79] hover:bg-[#FE4B79] hover:text-white font-semibold px-8 py-4 rounded-full transition-all duration-300"
                >
                  Speak with Expert
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Travel Quiz Modal */}
      <TravelQuizModal 
        isOpen={isQuizModalOpen} 
        onClose={() => setIsQuizModalOpen(false)} 
      />
    </section>
  );
};

export default TravelTypes;